import React from "react";

const accentMap = {
  cyan: "rgb(var(--accent-cyan-rgb))",
  blue: "rgb(var(--accent-blue-rgb))",
  violet: "rgb(var(--accent-violet-rgb))",
};

export const AccentDot = ({ accent = "cyan", className = "" }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${className}`}
    style={{ background: accentMap[accent], boxShadow: `0 0 10px ${accentMap[accent]}` }}
  />
);

export const SectionHeader = ({ eyebrow, title, action }) => (
  <div className="flex items-end justify-between gap-3 mb-5">
    <div>
      {eyebrow && <div className="section-label">{eyebrow}</div>}
      <h2 className="mt-2 font-display text-[22px] sm:text-[26px] font-light tracking-tight text-ink">{title}</h2>
    </div>
    {action}
  </div>
);

export const StatChip = ({ label, value, delta, accent = "cyan", icon: Icon }) => (
  <div className="glass rounded-2xl p-4 hairline flex items-center justify-between gap-3 min-w-0">
    <div className="min-w-0">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted truncate">{label}</div>
      <div className="mt-1 font-display text-[28px] font-light leading-none tabular-nums text-ink">{value}</div>
      {delta && <div className="mt-1 font-mono text-[10.5px] tracking-[0.18em] uppercase text-aura-cyan">{delta}</div>}
    </div>
    {Icon && (
      <div className="w-10 h-10 rounded-xl border border-panel/10 grid place-items-center text-aura-cyan bg-aura-cyan/5">
        <Icon size={16} strokeWidth={1.5} />
      </div>
    )}
  </div>
);

export const MemoryChip = ({ node, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left node-card p-4 hover:translate-y-[-1px] transition-transform"
    data-testid={`memory-chip-${node.id}`}
  >
    <div className="relative z-10 flex items-center justify-between">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
        <AccentDot accent={node.accent} />
        {node.category}
      </div>
      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-whisper-muted">{node.saved_at}</span>
    </div>
    <div className="relative z-10 mt-2 font-display text-[16px] font-medium text-ink leading-tight">{node.label}</div>
    <p className="relative z-10 mt-1 text-[13px] text-whisper line-clamp-2">{node.detail}</p>
  </button>
);

export const ScenarioCard = ({ s, onStart }) => (
  <div className="glass rounded-2xl p-5 edge-glow hairline flex flex-col">
    <div className="flex items-center justify-between">
      <span className="pill">{s.tag}</span>
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{s.duration}</span>
    </div>
    <h4 className="mt-4 font-display text-[20px] font-light tracking-tight text-ink leading-tight">{s.title}</h4>
    <p className="mt-2 text-whisper text-[13.5px] leading-[1.5] flex-1">{s.hook}</p>
    <div className="mt-4 flex items-center justify-between gap-2">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{s.difficulty}</span>
      <button onClick={onStart} className="btn-primary py-2 px-4 text-[10px]" data-testid={`scenario-start-${s.id}`}>start</button>
    </div>
  </div>
);

export const Sparkline = ({ data, width = 120, height = 36, color = "rgb(var(--accent-cyan-rgb))" }) => {
  if (!data?.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(1, max - min);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * height * 0.85 - height * 0.075;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className="block">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts.join(" ")} style={{ filter: "drop-shadow(0 0 4px " + color + ")" }} />
    </svg>
  );
};
